import React from 'react';
import { useTranslation } from 'react-i18next';
import { useGetOrganizationTitles } from 'api/queries/organizations/titles';
import MemberCard from './components/MemberCard';
import MemberCardSkeleton from './components/MemberCardSkeleton';

const TeamList = ({
  organizationId,
  mobile,
  user
}: {
  organizationId: string;
  mobile?: boolean;
  user?: any;
}) => {
  const { t } = useTranslation(['organizations']);
  const { data: titles, isLoading } = useGetOrganizationTitles({
    organizationId,
    useUnauthenticatedEndpoint: !user
  });

  if (isLoading || !titles) {
    return (
      <div className="z-10 flex flex-col w-full ml-6 pt-6">
        <div
          className={`flex justify-start w-full flex-wrap py-4 ${
            mobile ? 'flex-col' : 'flex-row gap-6'
          }`}
        >
          {Array.from({ length: 3 }).map((_, index) => (
            <MemberCardSkeleton key={index} />
          ))}
        </div>
      </div>
    );
  }

  // Group users by their ID and collect all their titles
  const userMap = new Map<string, { user: any; titles: string[] }>();

  titles.items.forEach((title) => {
    if (title.users?.length > 0) {
      title.users.forEach((user) => {
        if (userMap.has(user.id)) {
          // User already exists, add this title to their list
          userMap.get(user.id)?.titles.push(title.name);
        } else {
          // New user, create entry
          userMap.set(user.id, {
            user,
            titles: [title.name]
          });
        }
      });
    }
  });

  const uniqueUsers = Array.from(userMap.values());
  const hasUsers = uniqueUsers.length > 0;

  return (
    <div className="z-10 flex flex-col w-full ml-6 pt-6">
      {hasUsers ? (
        <>
          <div
            className={`flex justify-start w-full flex-wrap py-4 ${
              mobile ? 'flex-col' : 'flex-row gap-6'
            }`}
          >
            {uniqueUsers.map(({ user, titles }) => (
              <MemberCard
                key={user.id}
                name={user.name}
                title={titles.join(', ')}
                avatar={user.photoUrl}
                email={user.email}
              />
            ))}
          </div>
        </>
      ) : (
        <p className="flex w-full justify-center text-lg text-gray-500 pt-8">
          {t('team.membersPrivate')}
        </p>
      )}
    </div>
  );
};

export default TeamList;
